// Keep the signed-in user's ROLE and CAPABILITIES honest between sign-ins.
//
// The auth slice hydrates from a snapshot written into localStorage at login,
// and the sidebar renders from that snapshot. So when an Admin changes what
// someone is — promoting Hana to Media Buying Team Leader, or switching one of
// a leader's tabs off — the server knows immediately and the person's own
// screen does not. Hana was given the leader role, the API started serving her
// the squad's data, and her sidebar went on being a plain buyer's: no My Team
// tab, and her team's clients arriving in a tab that still looked like a
// buyer's. The only cure was signing out and in again, which nobody thinks to
// do because nothing appears broken.
//
// /me already answers all of it, computed server-side on every request. This
// reconciles the snapshot with that answer on load, so a role change or a
// permission toggle lands on the next page view.
//
// Deliberately a narrow list, not a wholesale replace: the snapshot carries
// things /me does not (_id, personalClientId, pushEnabled, managedClients),
// and overwriting it with /me's shape would quietly drop them. `id` is also
// excluded — /me spells it `id` while the snapshot uses `_id`, and copying it
// across would leave two ids disagreeing.
export const IDENTITY_FIELDS = Object.freeze([
  "role",
  "name",
  "email",
  "initials",
  "isAdmin",
  "canViewMediaBuying",
  "canViewBudgetTracker",
  "canViewTeams",
  "canViewWeekendDoubles",
  "canViewSquads",
  "mbLeaderPerms",
]);

const same = (a, b) => {
  if (a === b) return true;
  // mbLeaderPerms is a flat boolean map; everything else is a scalar.
  if (a && b && typeof a === "object" && typeof b === "object") {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    return ka.length === kb.length && ka.every((k) => a[k] === b[k]);
  }
  return false;
};

// The fields of `me` that differ from the stored `user`, or null when nothing
// has changed. Returning null is what keeps this from dispatching on every
// render and looping.
export const identityPatch = (user, me) => {
  if (!user || !me) return null;
  const patch = {};
  for (const key of IDENTITY_FIELDS) {
    if (!(key in me)) continue; // /me did not speak to this field
    if (!same(user[key], me[key])) patch[key] = me[key];
  }
  return Object.keys(patch).length ? patch : null;
};
